import axios, { AxiosInstance } from 'axios'

/**
 * Cliente HTTP del service-tradein-logistic.
 *
 * Convención: TODAS las llamadas requieren idSeller.
 *   · GETs lo envían como query param `id_seller`
 *   · POSTs lo envían en el body
 *
 * Env var: VITE_SERVICE_LOGISTIC_BOX
 */

const RAW = (import.meta.env.VITE_SERVICE_LOGISTIC_BOX as string | undefined) ?? ''
const BASE_URL = RAW.replace(/\/+$/, '')

/**
 * Resuelve la URL de una etiqueta para abrirla/descargarla.
 * Las de DHL ya vienen absolutas (S3). Las internas se guardan como ruta
 * relativa del API (/labels/internalLabel/ID) → se le antepone el baseURL y se
 * agrega id_seller (el endpoint lo requiere).
 */
export const resolveLabelUrl = (pdfUrl: string | null, idSeller: number | null): string | null => {
  if (pdfUrl === null || pdfUrl === '') return null
  if (/^https?:\/\//.test(pdfUrl)) return pdfUrl
  const base = (BASE_URL ?? '').replace(/\/+$/, '')
  const sep = pdfUrl.includes('?') ? '&' : '?'
  return `${base}${pdfUrl}${sep}id_seller=${idSeller ?? ''}`
}

const client: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30_000
})

// Cache en memoria del catálogo geográfico CL (estático · no cambia en la sesión).
let clLocationsCache: ClRegion[] | null = null

export interface IApiReturn<T> {
  success: boolean
  message: string
  data: T | null
}

async function unwrap<T> (promise: Promise<{ data: IApiReturn<T> }>): Promise<T> {
  const { data } = await promise
  if (!data.success || data.data === null) {
    throw new Error(data.message || 'API error')
  }
  return data.data
}

// Igual que unwrap pero acepta data:null como respuesta válida (p. ej. una
// tienda que aún no tiene dirección: success=true, data=null). Solo lanza si
// success es false.
async function unwrapNullable<T> (promise: Promise<{ data: IApiReturn<T | null> }>): Promise<T | null> {
  const { data } = await promise
  if (!data.success) {
    throw new Error(data.message || 'API error')
  }
  return data.data
}

function assertSeller (idSeller: number | null | undefined): number {
  if (idSeller === null || idSeller === undefined || idSeller <= 0) {
    throw new Error('Seller no seleccionado')
  }
  return idSeller
}

// ===================== DASHBOARD =====================

export interface LabelsSummaryKpis {
  listas: number
  con_error: number
  pendientes: number
  total: number
}

export interface TodayStoreRow {
  id_store: number
  store: string
  seller_store_number: number | null
  seller_name: string | null
  entregados: number
  en_custodia: number
  en_espera: number
  en_camino: number
  discrepancias: number
  custodia_sin_sello: number
  custodia_con_sello: number
  porcentaje_encajado: number
  dias_desde_ultimo_retiro: number | null
  // Alerta principal (la más grave) · para ordenar y la barra de progreso.
  alerta: { tone: 'green' | 'amber' | 'rosa', label: string }
  // Todas las alertas activas · se muestran como chips por separado.
  alertas: Array<{ tone: 'green' | 'amber' | 'rosa', label: string }>
}

export const dashboardApi = {
  labelsSummary: async (idSeller: number | null): Promise<LabelsSummaryKpis> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<LabelsSummaryKpis>>('/dashboard/labelsSummary', { params: { id_seller: s } }))
  },
  todayByStore: async (idSeller: number | null, idStore: number | null = null): Promise<TodayStoreRow[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<TodayStoreRow[]>>('/dashboard/todayByStore', { params: { id_seller: s, ...(idStore != null ? { id_store: idStore } : {}) } }))
  },
  notifyEndOfDay: async (idSeller: number | null, userId: number | null): Promise<{ ok: boolean, notified: number, message: string }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ ok: boolean, notified: number, message: string }>>(
      '/notifications/endOfDay', { id_seller: s, user_id: userId }
    ))
  },
  // Envía por correo las alertas de UNA tienda al contacto de su dirección.
  notifyStore: async (
    idStore: number, store: string, alertas: Array<{ tone: string, label: string }>,
    idSeller: number | null, userId: number | null
  ): Promise<{ ok: boolean, sent: boolean, message: string }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ ok: boolean, sent: boolean, message: string }>>(
      '/notifications/notifyStore', { id_store: idStore, store, alertas, id_seller: s, user_id: userId }
    ))
  },
  // Notifica por correo a varias tiendas a la vez (botón "Notificar todas").
  notifyMany: async (
    items: Array<{ id_store: number, store: string, alertas: Array<{ tone: string, label: string }> }>,
    idSeller: number | null, userId: number | null
  ): Promise<{ ok: boolean, enviadas: number, omitidas: number, message: string }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ ok: boolean, enviadas: number, omitidas: number, message: string }>>(
      '/notifications/notifyMany', { id_seller: s, user_id: userId, items }
    ))
  },
  // Notificaciones que ve el jefe de tienda (de su tienda) + # sin leer.
  listNotifications: async (idStore: number, idSeller: number | null): Promise<NotificationsResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<NotificationsResult>>(
      '/notifications/list', { params: { id_seller: s, id_store: idStore } }
    ))
  },
  // Marca como leídas todas las notificaciones de la tienda.
  markNotificationsRead: async (idStore: number, idSeller: number | null): Promise<{ ok: boolean, updated: number }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ ok: boolean, updated: number }>>(
      '/notifications/markRead', { id_store: idStore, id_seller: s }
    ))
  }
}

export interface NotificationDto {
  id: number
  titulo: string | null
  mensaje: string
  tipo: string
  leida: boolean
  fecha: string
}

export interface NotificationsResult {
  items: NotificationDto[]
  unread: number
}

// ===================== ETIQUETAS =====================

export type LabelTodayEstado = 'lista' | 'generando' | 'pendiente' | 'error'

export interface LabelTodayRow {
  id_store: number
  store: string
  encargado: string | null
  estado: LabelTodayEstado
  // Avance del tracking de la etiqueta activa · null si aún no hay label_current.
  // Define el chip de la tabla principal: Creada / Retiro / En camino / Entregada.
  estado_local: 'lista' | 'recogida' | 'en_transito' | 'entregada' | null
  tracking_number: string | null
  pdf_url: string | null
  generada_a: string | null
  fecha_programada_pickup: string | null
  id_collection: number | null
  id_label_history: number | null
  error_message: string | null
  con_envio_semana: boolean
  tipo_retiro: 'dhl' | 'blueexpress' | 'interno' | null
  seal: string | null
  seal2: string | null
  // box_key de la caja ('default' base · '2','3'… manuales) · filtra "Ver envío".
  box_key: string | null
  pickup_id: string | null
  // Referencia de la caja que va en la etiqueta del courier (CAJA-CL-000012).
  referencia: string | null
}

export interface RetryPendingResult {
  intentadas: number
  exitosas: number
  fallidas: number
  detalles: Array<{
    id_collection: number
    id_store: number
    success: boolean
    tracking_number: string | null
    error_message: string | null
  }>
}

export interface RegenerateResult {
  id_collection: number
  success: boolean
  tracking_number: string | null
  pdf_url: string | null
  error_message: string | null
}

export interface EnvioEquipoRow {
  id_tradein: number
  folio: string | null
  equipo: string
  valor: number | null
  fecha_inclusion: string | null
  ejecutivo: string | null
}

export interface EnvioDetalle {
  id_collection: number
  id_store: number
  store: string
  tracking_number: string | null
  pdf_url: string | null
  estado: string
  regla_gatillo: number
  fecha_gatillo: string | null
  total_equipos: number
  equipos: EnvioEquipoRow[]
  // Referencia de la caja que va en la etiqueta del courier (CAJA-CL-000012).
  referencia: string | null
}

// Etiqueta de una caja (par de sellos) dentro de una recolección.
export interface BoxLabelRow {
  id: number
  id_collection: number
  box_key: string
  seal: string | null
  seal2: string | null
  tracking_number: string
  pdf_url: string | null
  estado_local: string
}

export interface SyncBoxLabelsResult {
  id_collection: number
  cajas_selladas: number
  etiquetas_creadas: number
  etiquetas_total: number
}

export const labelsApi = {
  today: async (idSeller: number | null, idStore: number | null = null): Promise<LabelTodayRow[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<LabelTodayRow[]>>('/labels/today', { params: { id_seller: s, ...(idStore != null ? { id_store: idStore } : {}) } }))
  },
  // Etiquetas (una por caja / par de sellos) de una recolección.
  boxLabels: async (idCollection: number, idSeller: number | null): Promise<BoxLabelRow[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<BoxLabelRow[]>>('/labels/byCollection', { params: { id_seller: s, id_collection: idCollection } }))
  },
  // Equipos que aún están en la etiqueta base · checklist para "Agregar etiqueta".
  baseEquipos: async (idCollection: number, idSeller: number | null): Promise<Array<{ id_tradein: number, folio: string | null, equipo: string }>> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<Array<{ id_tradein: number, folio: string | null, equipo: string }>>>(
      '/labels/baseEquipos', { params: { id_seller: s, id_collection: idCollection } }
    ))
  },
  // Crea una etiqueta adicional con los equipos elegidos (se mueven de la base a la nueva).
  addLabel: async (
    idCollection: number, idEquipos: number[], idSeller: number | null, userId: number | null = null
  ): Promise<{ tracking_number: string, pdf_url: string, box_key: string, cantidad: number }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ tracking_number: string, pdf_url: string, box_key: string, cantidad: number }>>(
      '/labels/addLabel', { id_seller: s, id_collection: idCollection, id_equipos: idEquipos, user_id: userId }
    ))
  },
  // Reconcilia/crea las etiquetas de las cajas selladas de una recolección.
  syncBoxLabels: async (idCollection: number, idStore: number, idSeller: number | null, userId: number | null = null): Promise<SyncBoxLabelsResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<SyncBoxLabelsResult>>(
      '/labels/syncBoxLabels', { id_seller: s, id_collection: idCollection, id_store: idStore, user_id: userId }
    ))
  },
  envioByCollection: async (idCollection: number, idSeller: number | null, seal?: string | null, seal2?: string | null, boxKey?: string | null, tracking?: string | null): Promise<EnvioDetalle> => {
    const s = assertSeller(idSeller)
    const params: Record<string, string | number> = { id_seller: s }
    // Identifica la caja: box_key · tracking (único por guía) · fallback por sellos.
    if (boxKey != null && boxKey !== '') params.box_key = boxKey
    if (tracking != null && tracking !== '') params.tracking = tracking
    if (seal != null && seal !== '') params.seal = seal
    if (seal2 != null && seal2 !== '') params.seal2 = seal2
    return await unwrap(client.get<IApiReturn<EnvioDetalle>>(
      `/labels/envioByCollection/${idCollection}`, { params }
    ))
  },
  retryPending: async (idSeller: number | null, userId: number | null): Promise<RetryPendingResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<RetryPendingResult>>(
      '/labels/retryPending', { id_seller: s, user_id: userId }
    ))
  },
  regenerate: async (
    idCollection: number,
    idSeller: number | null,
    userId: number | null,
    tipoRetiro: 'dhl' | 'interno' = 'dhl'
  ): Promise<RegenerateResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<RegenerateResult>>(
      '/labels/regenerate', { id_collection: idCollection, id_seller: s, user_id: userId, tipo_retiro: tipoRetiro }
    ))
  },
  // Retiros internos (#1): marcar salida de tienda / llegada a bodega
  markInternalCollection: async (
    idCollection: number,
    tipo: 'salida' | 'llegada',
    idSeller: number | null,
    userId: number | null
  ): Promise<{ id_collection: number, tipo: string, estado: string, message: string }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ id_collection: number, tipo: string, estado: string, message: string }>>(
      '/labels/markInternalCollection', { id_collection: idCollection, tipo, id_seller: s, user_id: userId }
    ))
  },
  // URL de la etiqueta interna imprimible (HTML)
  internalLabelUrl: (idCollection: number, idSeller: number | null): string => {
    const s = assertSeller(idSeller)
    return `${client.defaults.baseURL ?? ''}/labels/internalLabel/${idCollection}?id_seller=${s}`
  },
  // Genera una recolección ahora para una tienda (Principal / Reglas) · DHL o interno
  generateInternalCollection: async (
    idStore: number,
    idSeller: number | null,
    userId: number | null,
    tipo: 'dhl' | 'interno' = 'interno'
  ): Promise<GenerateNowResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<GenerateNowResult>>(
      '/labels/generateInternalCollection', { id_store: idStore, id_seller: s, user_id: userId, tipo }
    ))
  },
  // Hoja de ruta de retiros internos (pendientes + en ruta)
  internalRoute: async (idSeller: number | null): Promise<InternalRouteWorklist> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<InternalRouteWorklist>>(
      '/labels/internalRoute', { params: { id_seller: s } }
    ))
  },
  // Sugerencia de ruta con IA (orden + estimado + link Maps)
  suggestInternalRoute: async (idSeller: number | null, origen?: string): Promise<RouteSuggestion> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<RouteSuggestion>>(
      '/labels/suggestInternalRoute', { id_seller: s, origen }
    ))
  },
  // Tiendas con equipos en custodia sin dirección (tab "No generadas")
  sinDireccion: async (idSeller: number | null): Promise<StoreSinDireccion[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<StoreSinDireccion[]>>(
      '/labels/sinDireccion', { params: { id_seller: s } }
    ))
  },
  // Crea/actualiza la dirección principal de una tienda. Tras guardar reintenta
  // generar la recolección (respeta la regla DHL/interno de la tienda).
  upsertAddress: async (
    input: StoreAddressInput, idSeller: number | null, userId: number | null = null,
    generarRecoleccion = true
  ): Promise<UpsertAddressResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<UpsertAddressResult>>(
      '/stores/upsertAddress', { ...input, id_seller: s, user_id: userId, generar_recoleccion: generarRecoleccion }
    ))
  },
  // Carga manualmente la etiqueta DHL (PDF) de una recolección (sube a S3).
  uploadManualLabel: async (
    idCollection: number, idStore: number, pdfBase64: string, tracking: string,
    idSeller: number | null, userId: number | null = null
  ): Promise<{ tracking_number: string, pdf_url: string }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ tracking_number: string, pdf_url: string }>>(
      '/labels/uploadManualLabel', { id_collection: idCollection, id_store: idStore, pdf_base64: pdfBase64, tracking, id_seller: s, user_id: userId }
    ))
  },
  // Dirección principal de una tienda (para editar) · null si no tiene.
  getAddress: async (idStore: number, idSeller: number | null): Promise<StoreAddressDto | null> => {
    const s = assertSeller(idSeller)
    return await unwrapNullable(client.get<IApiReturn<StoreAddressDto | null>>(
      '/stores/address', { params: { id_seller: s, id_store: idStore } }
    ))
  },
  // Catálogo geográfico de Chile (región → comuna → localidad) para los selects
  // de dirección. Estático → se cachea en memoria tras la primera llamada.
  getLocations: async (): Promise<ClRegion[]> => {
    if (clLocationsCache !== null) return clLocationsCache
    const data = await unwrap(client.get<IApiReturn<ClRegion[]>>('/locations/cl'))
    clLocationsCache = data
    return data
  }
}

export interface StoreAddressDto {
  id: number
  id_store: number
  calle: string | null
  numero: string | null
  colonia: string | null
  ciudad: string | null
  estado_region: string | null
  cod_region: number | null
  cod_comuna: number | null
  nom_comuna: string | null
  cod_localidad: string | null
  nom_localidad: string | null
  codigo_postal: string | null
  pais: string | null
  referencia: string | null
  contacto_nombre: string | null
  contacto_telefono: string | null
  contacto_email: string | null
}

export interface UpsertAddressResult {
  created: boolean
  generacion?: {
    triggered: boolean
    motivo_no_trigger: string | null
    tracking_number: string | null
  }
}

export interface StoreSinDireccion {
  id_store: number
  store: string
  en_custodia: number
}

// Lee un File (PDF) y devuelve su contenido en base64 (sin el prefijo data:).
export const fileToBase64 = async (file: File): Promise<string> => {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const res = String(reader.result ?? '')
      resolve(res.replace(/^data:[^;]+;base64,/, ''))
    }
    reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'))
    reader.readAsDataURL(file)
  })
}

export interface StoreAddressInput {
  id_store: number
  calle: string
  numero?: string
  colonia?: string
  ciudad: string
  estado_region?: string
  // Códigos Blue Express (CL) · capturados por los selects en cascada. En MX no se envían.
  cod_region?: number
  cod_comuna?: number
  nom_comuna?: string
  cod_localidad?: string
  nom_localidad?: string
  codigo_postal: string
  // El país no se captura · lo asigna el backend según el país de operación.
  pais?: string
  referencia?: string
  contacto_nombre: string
  contacto_telefono: string
  contacto_email?: string
}

// ===================== CATÁLOGO GEOGRÁFICO CL =====================

export interface ClLocalidad { codLocalidad: string, nomLocalidad: string }
export interface ClComuna { codComuna: number, nomComuna: string, localidades: ClLocalidad[] }
export interface ClRegion { codRegion: number, nomRegion: string, comunas: ClComuna[] }

export type RouteStopEstado = 'por_retirar' | 'en_ruta'

export interface RouteStopDto {
  id_collection: number
  id_store: number
  store: string
  estado: RouteStopEstado
  equipos: number
  direccion: string
  direccion_corta: string
  referencia: string | null
  contacto_nombre: string | null
  contacto_telefono: string | null
  tracking_number: string | null
  fecha_programada_pickup: string | null
  etiqueta_url: string | null
}

export interface InternalRouteWorklist {
  fecha: string
  total_paradas: number
  total_equipos: number
  por_retirar: number
  en_ruta: number
  paradas: RouteStopDto[]
}

export interface RouteSuggestion {
  orden: number[]
  distancia_km: number | null
  duracion_min: number | null
  resumen: string
  maps_url: string
  fallback: boolean
}

// ---- Reparto interno de insumos (Bodega → tiendas) ----
export type SupplyRouteStopEstado = 'por_enviar' | 'en_ruta'

export interface SupplyRouteStopDto {
  id_shipment: number
  id_store: number
  store: string
  estado: SupplyRouteStopEstado
  contenido: string
  cant_caja: number
  cant_bolsa_a: number
  cant_bolsa_b: number
  cant_sello: number
  piezas: number
  direccion: string
  direccion_corta: string
  referencia: string | null
  contacto_nombre: string | null
  contacto_telefono: string | null
  tracking_number: string | null
}

export interface SupplyRouteWorklist {
  fecha: string
  total_paradas: number
  total_piezas: number
  por_enviar: number
  en_ruta: number
  paradas: SupplyRouteStopDto[]
}

export interface GenerateNowResult {
  id_store: number
  equipos_en_custodia: number
  triggered: boolean
  motivo_no_trigger: string | null
  id_collection: number | null
  tracking_number: string | null
}

// ===================== REGLAS (#13/#14) =====================

export type Distancia = 'cercana' | 'media' | 'lejana'

export interface EffectiveRule {
  // null = no configurado (regla apagada · sin defaults)
  min_equipos_inmediata: number | null
  min_equipos_semanal: number | null
  max_dias_sin_retiro: number | null
}

export interface StoreRuleDto {
  id_store: number
  store: string
  // Tienda encendida/apagada para recolección (gestionable solo en Reglas).
  activo: boolean
  // Curaduría: true = visible en la lista de Reglas · false = oculta (basura/duplicada).
  gestion: boolean
  // true = Reuse · false = DHL · null = no configurado (el admin debe elegir)
  recoleccion_interna: boolean | null
  distancia: Distancia | null
  notas: string | null
  // Día de recolección semanal · 1=Lun … 7=Dom · null = sin día asignado
  dia_recoleccion_semanal: number | null
  // Cadencia semanal · cada cuántas semanas aplica la regla 2 (1 = cada semana)
  cada_n_semanas: number
  override: {
    min_equipos_inmediata: number | null
    min_equipos_semanal: number | null
    max_dias_sin_retiro: number | null
  }
  efectivo: EffectiveRule
  // Safety stock por insumo · override por tienda (null = hereda el default)
  safety: SafetyByInsumo
  // Default del catálogo por insumo (placeholder)
  safety_default: { caja: number, bolsa_a: number, bolsa_b: number, sello: number }
}

export interface SafetyByInsumo {
  caja: number | null
  bolsa_a: number | null
  bolsa_b: number | null
  sello: number | null
}

export interface UpsertRuleInput {
  id_store: number
  min_equipos_inmediata: number | null
  min_equipos_semanal: number | null
  max_dias_sin_retiro: number | null
  // true=Reuse · false=DHL · null = no configurado (requerido para operar)
  recoleccion_interna: boolean | null
  distancia: Distancia | null
  notas: string | null
  // 1=Lun … 7=Dom · null limpia el día
  dia_recoleccion_semanal?: number | null
  // Cadencia semanal · cada cuántas semanas (1 = cada semana)
  cada_n_semanas?: number | null
  // Encendido/apagado de la tienda · se guarda con el botón (no auto).
  activo?: boolean
  // Safety por insumo (null por campo limpia el override)
  safety?: SafetyByInsumo
}

export const rulesApi = {
  list: async (idSeller: number | null): Promise<StoreRuleDto[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<StoreRuleDto[]>>('/rules', { params: { id_seller: s } }))
  },
  upsert: async (input: UpsertRuleInput, idSeller: number | null, userId: number | null): Promise<void> => {
    const s = assertSeller(idSeller)
    console.log('Guardando regla', { ...input, id_seller: s, user_id: userId })
    await unwrap(client.post<IApiReturn<null>>('/rules/upsert', { ...input, id_seller: s, user_id: userId }))
  },
  // Guardado masivo ("Guardar todo") · un solo correo de periodicidad agrupado.
  upsertMany: async (
    items: UpsertRuleInput[], idSeller: number | null, userId: number | null
  ): Promise<{ guardadas: number, errores: Array<{ id_store: number, error: string }> }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ guardadas: number, errores: Array<{ id_store: number, error: string }> }>>(
      '/rules/upsertMany', { items, id_seller: s, user_id: userId }
    ))
  }
}

// ===================== DISCREPANCIAS (#2) =====================

export type EquipoEstado = 'recibido' | 'discrepancia' | 'pendiente'

export interface DiscrepancyEquipoDto {
  id_tradein: number
  equipo: string
  folio: string | null
  serie: string | null
  seal: string | null
  seal2: string | null
  estado: EquipoEstado
  was_resolved: boolean
  was_processed: boolean
}

export interface DiscrepancyCajaDto {
  id_collection: number
  store: string
  tipo_retiro: 'dhl' | 'blueexpress' | 'interno'
  folio_caja: string | null
  fecha_entregada: string | null
  total_enviados: number
  total_recibidos: number
  total_discrepancias: number
  equipos: DiscrepancyEquipoDto[]
}

export type ReceptionEstado = 'completo' | 'con_discrepancia' | 'sin_abrir' | 'parcial'

export interface ReceptionStoreDto {
  id_store: number
  store: string
  total_cajas: number
  cajas_sin_abrir: number
  enviados: number
  recibidos: number
  discrepancias: number
  pendientes: number
  estado: ReceptionEstado
}

export const discrepanciesApi = {
  // Resumen de recepción por tienda (fila principal)
  list: async (idSeller: number | null): Promise<ReceptionStoreDto[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<ReceptionStoreDto[]>>('/discrepancies', { params: { id_seller: s } }))
  },
  // Detalle por tienda (cajas + equipos)
  detail: async (idStore: number, idSeller: number | null): Promise<DiscrepancyCajaDto[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<DiscrepancyCajaDto[]>>(
      '/discrepancies/detail', { params: { id_seller: s, id_store: idStore } }
    ))
  },
  // Alias usado por el Principal nuevo (mismo endpoint que detail).
  detailByStore: async (idStore: number, idSeller: number | null): Promise<DiscrepancyCajaDto[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<DiscrepancyCajaDto[]>>(
      '/discrepancies/detail', { params: { id_seller: s, id_store: idStore } }
    ))
  },
  changeSeals: async (
    idTradein: number,
    seal: string,
    seal2: string | null,
    idSeller: number | null
  ): Promise<{ id_tradein: number, updated: boolean }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ id_tradein: number, updated: boolean }>>(
      '/discrepancies/changeSeals', { id_tradein: idTradein, seal, seal2, id_seller: s }
    ))
  }
}

// ===================== TRACKING =====================

// 'recogida' = Retiro (caja recogida en la tienda). 'atrasada' quedó retirado.
export type EstadoLocal = 'lista' | 'recogida' | 'en_transito' | 'entregada' | 'atrasada'

export interface TrackingActiveKpis {
  total_activos: number
  retirado: number
  en_transito: number
  entregado: number
  atrasado: number
}

export interface TrackingEvent {
  id: number
  evento_codigo: string | null
  descripcion: string | null
  ubicacion: string | null
  fecha_evento: string
}

export interface TrackingActiveItem {
  id_label_current: number
  id_store: number
  id_collection: number | null
  store: string
  tracking_number: string
  pdf_url: string | null
  estado_local: EstadoLocal
  estado_dhl: string | null
  seal: string | null
  seal2: string | null
  // Equipos que HOY van en esta caja (recalculado por box_index · no es fijo).
  cantidad_equipos: number
  ultimo_sync_dhl_at: string | null
  ultimo_evento: TrackingEvent | null
  courier: string
  // Referencia de la caja que va en la etiqueta del courier (CAJA-CL-000012).
  referencia: string | null
}

export interface TrackingDetail {
  tracking_number: string
  id_label_history: number
  id_collection: number
  id_store: number
  store: string
  pdf_url: string | null
  estado_local: EstadoLocal | null
  estado_dhl: string | null
  ultimo_sync_dhl_at: string | null
  cantidad_equipos_estimada: number | null
  cantidad_equipos_real: number | null
  fecha_picked_up: string | null
  fecha_entregada: string | null
  eta_entrega: string | null
  ourReference: string
  origen: string
  destino: string
  courier: string
  eventos: TrackingEvent[]
}

export interface TrackingSyncResult {
  tracking_number: string
  events_added: number
  estado_dhl: string | null
  estado_local: EstadoLocal
  warning: string | null
}

export const trackingApi = {
  active: async (idSeller: number | null): Promise<{ kpis: TrackingActiveKpis, items: TrackingActiveItem[] }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<{ kpis: TrackingActiveKpis, items: TrackingActiveItem[] }>>(
      '/tracking/active', { params: { id_seller: s } }
    ))
  },
  detail: async (trackingNumber: string, idSeller: number | null): Promise<TrackingDetail> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<TrackingDetail>>(
      `/tracking/${encodeURIComponent(trackingNumber)}`, { params: { id_seller: s } }
    ))
  },
  sync: async (trackingNumber: string, idSeller: number | null, userId: number | null): Promise<TrackingSyncResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<TrackingSyncResult>>(
      `/tracking/${encodeURIComponent(trackingNumber)}/sync`, { id_seller: s, user_id: userId }
    ))
  }
}

// ===================== STORES =====================

export interface StoreDetail {
  id_store: number
  store: string
  code: string | null
  dia_recoleccion_semanal: number | null
  last_picked_up_at: string | null
  dias_desde_ultimo_retiro: number | null
  activo_recoleccion: number
  kpis: {
    entregados: number
    en_custodia: number
    en_camino: number
    porcentaje_encajado: number
    sin_custodia: number
    custodia_sin_sello: number
    custodia_con_sello: number
    listo_para_retiro: boolean
  }
}

export interface EquipoDiaRow {
  id_tradein: number
  folio: string | null
  // Par de sellos de la caja del equipo.
  seal: string | null
  seal2: string | null
  equipo: string
  estado: 'entregado' | 'custodia' | 'en_camino'
  // ¿El equipo está dentro de una recolección activa? true = Asignado · false = Sin asignar.
  asignado: boolean
  // Último estado del ciclo de caja (EN_ESPERA/EN_TRANSITO/RECIBIDO_BODEGA/NO_RECIBIDO) o null.
  box_state: string | null
  // Discrepancia sin resolver en ti_folio_discrepancies (fuente de verdad, igual que Arribos).
  con_discrepancia: boolean
  fecha_cotizacion: string | null
  fecha_entregado: string | null
  fecha_custodia: string | null
  ejecutivo: string | null
  valor: number | null
  // Motivo de alerta (null si no tiene) · 'Falta sello' | 'Sin custodia' | 'Discrepancia'.
  alerta: string | null
  // Referencia de la etiqueta donde va el equipo (CAJA-…) · null si no tiene.
  etiqueta: string | null
  // Número de tracking del courier de esa etiqueta · null si no tiene.
  tracking: string | null
}

export const storesApi = {
  detail: async (idStore: number, idSeller: number | null): Promise<StoreDetail> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<StoreDetail>>(`/stores/${idStore}`, { params: { id_seller: s } }))
  },
  equiposToday: async (idStore: number, idSeller: number | null): Promise<EquipoDiaRow[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<EquipoDiaRow[]>>(
      `/stores/${idStore}/equiposToday`, { params: { id_seller: s } }
    ))
  },
  // Línea de tiempo del ciclo de caja de un dispositivo (RN-29).
  deviceTimeline: async (idTradein: number): Promise<DeviceTimelineRow[]> => {
    return await unwrap(client.get<IApiReturn<DeviceTimelineRow[]>>(
      '/labels/deviceTimeline', { params: { id_tradein: idTradein } }
    ))
  }
}

export interface DeviceTimelineRow {
  id: number
  id_tradein: number
  evento: string
  id_collection: number | null
  box_key: string | null
  actor_tipo: string
  id_user: number | null
  fuente: string
  motivo: string | null
  created_at: string
}

// ===================== INCUMPLIMIENTOS =====================

export interface IncumplimientosKpis {
  tiendas_con_atraso: number
  equipos_atrasados: number
  dias_promedio: number
  equipo_mas_antiguo_dias: number | null
  tiendas_sin_direccion: number
}

export type DiagnosticoCausa =
  | 'sin_cajas_ni_sellos'
  | 'sin_sellos'
  | 'sin_cajas'
  | 'bolsas_a_bajas'
  | 'bolsas_b_bajas'
  | 'bolsas_bajas'
  | 'esperando_retiro'
  | 'operativa_lenta'
  | 'sin_direccion'
  | 'error_dhl'
  | 'limite_tiempo'
  | 'limite_inmediato'
  | 'limite_semanal'
  | 'caja_atrasada'

export interface Diagnostico {
  tone: 'amber' | 'rosa'
  label: string
  causa: DiagnosticoCausa
}

export interface IncumplimientoStoreRow {
  id_store: number
  store: string
  entregados: number
  custodia: number
  equipo_mas_antiguo: {
    id_tradein: number | null
    folio: string | null
    equipo: string | null
    dias: number
  } | null
  // Stock actual de los 4 insumos (InventoryCell definido en la sección SUPPLIES)
  stock: InventoryCell[]
  diagnostico: Diagnostico
  causas: Diagnostico[]
  dias_sin_retiro: number | null
  sin_direccion: boolean
}

export interface IncumplimientoEquipoRow {
  id_tradein: number
  folio: string | null
  equipo: string
  estado: 'entregado' | 'custodia'
  dias_en_estado: number
  fecha_cotizacion: string | null
  valor: number | null
  folio_bolsa: string | null
  ejecutivo: string | null
}

export interface IncumplimientoStoreDetail {
  id_store: number
  store: string
  last_picked_up_at: string | null
  dias_desde_ultimo_retiro: number | null
  contacto_nombre: string | null
  contacto_telefono: string | null
  equipos: IncumplimientoEquipoRow[]
  // Stock actual de los 4 insumos · alimenta el bloque "Causa probable"
  stock: InventoryCell[]
  diagnostico: Diagnostico
}

export const incumplimientosApi = {
  list: async (idSeller: number | null): Promise<{ kpis: IncumplimientosKpis, rows: IncumplimientoStoreRow[] }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<{ kpis: IncumplimientosKpis, rows: IncumplimientoStoreRow[] }>>(
      '/incumplimientos', { params: { id_seller: s } }
    ))
  },
  byStore: async (idStore: number, idSeller: number | null, minDias = 7): Promise<IncumplimientoStoreDetail> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<IncumplimientoStoreDetail>>(
      `/incumplimientos/${idStore}`, { params: { id_seller: s, minDias } }
    ))
  }
}

// ===================== CALENDAR =====================

export interface CalendarDayActivity {
  fecha: string
  collections: number
  picked_ups: number
  delivered: number
  tone: 'gray' | 'blue' | 'green' | 'amber'
}

export const calendarApi = {
  activity: async (idSeller: number | null, from: string, to: string): Promise<CalendarDayActivity[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<CalendarDayActivity[]>>(
      '/calendar/activity', { params: { id_seller: s, from, to } }
    ))
  }
}

// ===================== INSUMOS (SUPPLIES) =====================
//
// Espeja los DTOs de SupplyView.ts del service-tradein-logistic.
// Convención de rutas: el último segmento del path == function name del yml.

export type InsumoCodigo = 'caja' | 'bolsa_a' | 'bolsa_b' | 'sello'
export type SupplyRequestEstado = 'solicitada' | 'aprobada' | 'rechazada' | 'con_error' | 'pendiente' | 'despachada'
export type SupplyRequestOrigen = 'manual' | 'automatica'
export type SupplyRequestPrioridad = 'normal' | 'urgente' | 'critica'
export type SupplyShipmentEstadoLocal = 'lista' | 'en_transito' | 'entregada' | 'atrasada'

// ---- Inventario ----

export interface InsumoKpi {
  codigo: InsumoCodigo
  nombre: string
  en_tiendas: number
  en_transito: number
  objetivo_total: number
  porcentaje_optimo: number
}

export interface InventorySummary {
  total_tiendas: number
  insumos: InsumoKpi[]
  tiendas_stock_bajo: number
}

export interface InventoryCell {
  codigo: InsumoCodigo
  cantidad_actual: number
  safety_stock: number
  bajo_safety: boolean
  porcentaje: number
}

export interface InventoryStoreRow {
  id_store: number
  store: string
  celdas: InventoryCell[]
  ultimo_restock_at: string | null
  tiene_alerta: boolean
}

export interface ReportInventoryResult {
  id_store: number
  aplicados: Array<{
    codigo: InsumoCodigo
    cantidad_anterior: number
    cantidad_reportada: number
    delta: number
  }>
}

// ---- Solicitudes ----

export interface SupplyRequestItemDto {
  codigo: InsumoCodigo
  nombre: string
  cantidad_solicitada: number
  stock_actual_snapshot: number | null
  safety_stock_snapshot: number | null
  cantidad_sugerida: number | null
}

export interface SupplyRequestDto {
  id_request: number
  id_store: number
  store: string
  origen: SupplyRequestOrigen
  estado: SupplyRequestEstado
  prioridad: SupplyRequestPrioridad
  motivo: string | null
  notas: string | null
  fecha_solicitud: string
  fecha_resolucion: string | null
  solicitada_por_user_id: number | null
  solicitada_por_nombre: string | null
  gestionada_por_nombre: string | null
  id_shipment: number | null
  tracking_number: string | null
  pdf_url: string | null
  tipo_envio: 'dhl' | 'interno' | null
  // Fecha en que se generó/despachó la etiqueta del envío de insumos (ISO).
  fecha_despacho: string | null
  // Fecha programada de envío (próximo día hábil) · para preparar los insumos.
  fecha_programada_envio: string | null
  // true cuando el envío asociado ya llegó a la tienda (solicitud finalizada)
  envio_entregado: boolean
  // true cuando el envío ya fue recogido por el courier (en tránsito) · hasta
  // entonces la solicitud se muestra como "Creada" aunque la etiqueta exista.
  envio_en_camino: boolean
  tiene_direccion: boolean
  // Referencia del envío que va en la etiqueta del courier (INS-CL-000133) · null si aún no hay envío.
  referencia: string | null
  items: SupplyRequestItemDto[]
}

export interface SupplyRequestEventDto {
  id: number
  tipo: string
  origen: string | null
  descripcion: string | null
  fecha: string
  por: string | null
  // Snapshot de cantidades en ese cambio · null si el evento no lleva cantidades
  items: Array<{ codigo: string, cantidad: number }> | null
}

export interface SupplyRequestHistory {
  eventos: SupplyRequestEventDto[]
  solicitudes: SupplyRequestDto[]
}

// ---- Proyecciones IA ----
export type ProjectionFuente = 'ia' | 'safety_stock'
export type ProjectionEstado = 'en_revision' | 'aceptada' | 'descartada'
export type ProjectionConfianza = 'alta' | 'media' | 'baja'

export interface ProjectionItemDto {
  codigo: string
  nombre: string
  cantidad_sugerida: number
  stock_actual_snapshot: number | null
  safety_stock_snapshot: number | null
  dias_cobertura: number | null
}

export interface ProjectionDto {
  id_projection: number
  id_store: number
  store: string
  fuente: ProjectionFuente
  estado: ProjectionEstado
  fecha_generacion: string
  fecha_quiebre_estimada: string | null
  confianza: ProjectionConfianza | null
  justificacion: string | null
  tiene_direccion: boolean
  id_request: number | null
  items: ProjectionItemDto[]
}

export interface AcceptProjectionResult {
  id_projection: number
  id_request: number
  estado: ProjectionEstado
}

export interface GenerateProjectionsResult {
  generadas: number
  por_ia: number
  por_safety_stock: number
  con_fallback: number
  tiendas_evaluadas: number
}

export interface CreateRequestResult {
  id_request: number
  id_store: number
  estado: SupplyRequestEstado
  items_creados: number
}

export interface ApproveRequestResult {
  id_request: number
  estado: SupplyRequestEstado
  id_shipment: number | null
  tracking_number: string | null
  pdf_url: string | null
  error_message: string | null
}

export interface RejectRequestResult {
  id_request: number
  estado: SupplyRequestEstado
}

export interface EvaluateAutoResult {
  tiendas_evaluadas: number
  solicitudes_creadas: number
  detalles: Array<{
    id_store: number
    store: string
    codigo: InsumoCodigo
    stock_actual: number
    safety_stock: number
    cantidad_sugerida: number
    id_request: number
  }>
}

// ---- Envíos / tracking ----

export interface SupplyShipmentKpis {
  total_activos: number
  en_transito: number
  entregados: number
  atrasados: number
}

export interface SupplyShipmentItem {
  id_shipment: number
  id_store: number
  store: string
  tracking_number: string | null
  estado_local: SupplyShipmentEstadoLocal
  estado_dhl: string | null
  contenido_resumen: string | null
  eta_entrega: string | null
  ultimo_sync_dhl_at: string | null
}

export interface SupplyShipmentEventDto {
  id: number
  evento_codigo: string | null
  descripcion: string | null
  ubicacion: string | null
  fecha_evento: string
}

export interface SupplyShipmentDetail {
  id_shipment: number
  id_store: number
  store: string
  tracking_number: string | null
  pdf_url: string | null
  estado_local: SupplyShipmentEstadoLocal
  estado_dhl: string | null
  contenido_resumen: string | null
  origen: string
  destino: string
  eta_entrega: string | null
  fecha_despacho: string
  fecha_entregada: string | null
  eventos: SupplyShipmentEventDto[]
}

export interface SupplyShipmentSyncResult {
  tracking_number: string
  events_added: number
  estado_dhl: string | null
  estado_local: SupplyShipmentEstadoLocal
  recepcion_aplicada: boolean
  warning: string | null
}

// ---- Inputs ----

export interface SupplyItemInput {
  codigo: InsumoCodigo
  cantidad: number
}

export interface RecommendationItem {
  codigo: InsumoCodigo
  demanda: number
  stock_actual: number
  recomendado: number
  tope: number
}

export interface SupplyRecommendation {
  id_store: number
  equipos_base: number
  equipos_proyectados: number
  items: RecommendationItem[]
}

export interface CreateRequestInput {
  id_store: number
  items: SupplyItemInput[]
  prioridad?: SupplyRequestPrioridad
  motivo?: string
  notas?: string
}

export const suppliesApi = {
  // Inventario
  inventorySummary: async (idSeller: number | null): Promise<InventorySummary> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<InventorySummary>>(
      '/supplies/inventorySummary', { params: { id_seller: s } }
    ))
  },
  inventoryByStore: async (idSeller: number | null, idStore: number | null = null): Promise<InventoryStoreRow[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<InventoryStoreRow[]>>(
      '/supplies/inventoryByStore', { params: { id_seller: s, ...(idStore != null ? { id_store: idStore } : {}) } }
    ))
  },
  reportInventory: async (
    idStore: number,
    idSeller: number | null,
    items: SupplyItemInput[],
    userId: number | null
  ): Promise<ReportInventoryResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<ReportInventoryResult>>(
      '/supplies/reportInventory', { id_store: idStore, id_seller: s, user_id: userId, items }
    ))
  },

  // Solicitudes
  requests: async (
    idSeller: number | null,
    filtros: { estado?: string, origen?: string } = {}
  ): Promise<SupplyRequestDto[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<SupplyRequestDto[]>>(
      '/supplies/supplyRequests', { params: { id_seller: s, ...filtros } }
    ))
  },
  createRequest: async (
    input: CreateRequestInput,
    idSeller: number | null,
    userId: number | null
  ): Promise<CreateRequestResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<CreateRequestResult>>(
      '/supplies/createSupplyRequest', { ...input, id_seller: s, user_id: userId }
    ))
  },
  approveRequest: async (
    idRequest: number,
    idSeller: number | null,
    userId: number | null
  ): Promise<ApproveRequestResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<ApproveRequestResult>>(
      '/supplies/approveSupplyRequest', { id_request: idRequest, id_seller: s, user_id: userId }
    ))
  },
  rejectRequest: async (
    idRequest: number,
    idSeller: number | null,
    userId: number | null,
    motivo: string | null = null
  ): Promise<RejectRequestResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<RejectRequestResult>>(
      '/supplies/rejectSupplyRequest', { id_request: idRequest, id_seller: s, user_id: userId, motivo }
    ))
  },
  evaluateAuto: async (idSeller: number | null): Promise<EvaluateAutoResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<EvaluateAutoResult>>(
      '/supplies/evaluateAutoRequests', { id_seller: s }
    ))
  },
  // Edita cantidades de una solicitud pendiente y regenera la etiqueta.
  editRequest: async (
    idRequest: number,
    items: Array<{ codigo: InsumoCodigo, cantidad: number }>,
    idSeller: number | null,
    userId: number | null
  ): Promise<{ estado: SupplyRequestEstado, tracking_number: string | null, error_message: string | null }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ estado: SupplyRequestEstado, tracking_number: string | null, error_message: string | null }>>(
      '/supplies/editSupplyRequest', { id_request: idRequest, items, id_seller: s, user_id: userId }
    ))
  },
  // Despacha (bloquea) la solicitud.
  dispatchRequest: async (
    idRequest: number,
    idSeller: number | null,
    userId: number | null
  ): Promise<{ estado: SupplyRequestEstado }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ estado: SupplyRequestEstado }>>(
      '/supplies/dispatchSupplyRequest', { id_request: idRequest, id_seller: s, user_id: userId }
    ))
  },
  // Reintenta la etiqueta de una solicitud con error (re-dispara DHL/interno).
  retryLabel: async (
    idRequest: number, idSeller: number | null, userId: number | null
  ): Promise<{ estado: SupplyRequestEstado, tracking_number: string | null, error_message: string | null }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ estado: SupplyRequestEstado, tracking_number: string | null, error_message: string | null }>>(
      '/supplies/retrySupplyLabel', { id_request: idRequest, id_seller: s, user_id: userId }
    ))
  },
  // Carga manualmente la etiqueta DHL (PDF) del envío de una solicitud (sube a S3).
  uploadManualLabel: async (
    idRequest: number, pdfBase64: string, tracking: string, idSeller: number | null, userId: number | null
  ): Promise<{ id_shipment: number, tracking_number: string, pdf_url: string }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ id_shipment: number, tracking_number: string, pdf_url: string }>>(
      '/supplies/uploadManualSupplyLabel', { id_request: idRequest, pdf_base64: pdfBase64, tracking, id_seller: s, user_id: userId }
    ))
  },
  // Historial de la tienda (timeline + solicitudes históricas).
  requestHistory: async (
    idStore: number,
    idSeller: number | null,
    idRequest?: number
  ): Promise<SupplyRequestHistory> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<SupplyRequestHistory>>(
      '/supplies/supplyRequestHistory', { params: { id_seller: s, id_store: idStore, id_request: idRequest } }
    ))
  },
  recommendation: async (
    idStore: number,
    idSeller: number | null,
    // Opcional · si no se pasa, el backend usa el stock del sistema (cantidad_actual).
    stock?: { caja: number, bolsa_a: number, bolsa_b: number, sello: number }
  ): Promise<SupplyRecommendation> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<SupplyRecommendation>>(
      '/supplies/recommendation', { id_store: idStore, id_seller: s, ...(stock !== undefined ? { stock } : {}) }
    ))
  },

  // Envíos / tracking
  shipments: async (idSeller: number | null): Promise<{ kpis: SupplyShipmentKpis, items: SupplyShipmentItem[] }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<{ kpis: SupplyShipmentKpis, items: SupplyShipmentItem[] }>>(
      '/supplies/supplyShipments', { params: { id_seller: s } }
    ))
  },
  shipmentDetail: async (trackingNumber: string, idSeller: number | null): Promise<SupplyShipmentDetail> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<SupplyShipmentDetail>>(
      `/supplies/supplyShipmentDetail/${encodeURIComponent(trackingNumber)}`, { params: { id_seller: s } }
    ))
  },
  syncShipment: async (
    trackingNumber: string,
    idSeller: number | null,
    userId: number | null
  ): Promise<SupplyShipmentSyncResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<SupplyShipmentSyncResult>>(
      `/supplies/syncSupplyShipment/${encodeURIComponent(trackingNumber)}`, { id_seller: s, user_id: userId }
    ))
  },

  // Proyecciones IA
  projections: async (
    idSeller: number | null,
    fuente: ProjectionFuente = 'ia'
  ): Promise<ProjectionDto[]> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<ProjectionDto[]>>(
      '/supplies/projections', { params: { id_seller: s, fuente } }
    ))
  },
  acceptProjection: async (
    idProjection: number,
    idSeller: number | null,
    userId: number | null,
    cantidades?: Partial<Record<string, number>>
  ): Promise<AcceptProjectionResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<AcceptProjectionResult>>(
      '/supplies/acceptProjection', { id_projection: idProjection, id_seller: s, user_id: userId, cantidades }
    ))
  },
  dismissProjection: async (
    idProjection: number,
    idSeller: number | null,
    userId: number | null
  ): Promise<void> => {
    const s = assertSeller(idSeller)
    await unwrap(client.post<IApiReturn<null>>(
      '/supplies/dismissProjection', { id_projection: idProjection, id_seller: s, user_id: userId }
    ))
  },
  generateProjections: async (idSeller: number | null): Promise<GenerateProjectionsResult> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<GenerateProjectionsResult>>(
      '/supplies/generateProjections', { id_seller: s }
    ))
  },

  // ---- Reparto interno de insumos ----
  // URL de la etiqueta imprimible del envío de insumos (HTML)
  supplyLabelUrl: (idShipment: number, idSeller: number | null): string => {
    const s = assertSeller(idSeller)
    return `${client.defaults.baseURL ?? ''}/supplies/supplyLabel/${idShipment}?id_seller=${s}`
  },
  // Hoja de ruta de reparto interno (por enviar + en ruta)
  internalRoute: async (idSeller: number | null): Promise<SupplyRouteWorklist> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.get<IApiReturn<SupplyRouteWorklist>>(
      '/supplies/supplyInternalRoute', { params: { id_seller: s } }
    ))
  },
  // Sugerencia de ruta de reparto con IA
  suggestRoute: async (idSeller: number | null, origen?: string): Promise<RouteSuggestion> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<RouteSuggestion>>(
      '/supplies/suggestSupplyRoute', { id_seller: s, origen }
    ))
  },
  // Marca salida de bodega ('salida') o entrega en tienda ('entrega', suma inventario)
  markInternal: async (
    idShipment: number,
    tipo: 'salida' | 'entrega',
    idSeller: number | null,
    userId: number | null
  ): Promise<{ estado_local?: string, recepcion_aplicada?: boolean }> => {
    const s = assertSeller(idSeller)
    return await unwrap(client.post<IApiReturn<{ estado_local?: string, recepcion_aplicada?: boolean }>>(
      '/supplies/markInternalSupply', { id_shipment: idShipment, tipo, id_seller: s, user_id: userId }
    ))
  }
}
